import { useState, useContext, useMemo, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogAction from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import DataSourceContext from "../../context/DataSourceMngContext";
import { usePlantsList } from "src/api/userInfo";
import { CircularProgress } from "@mui/material";

const INITFORMDATA = {
  company_id: null,
  plant_id: null,
  name_en: "",
  name_th: "",
  description: "",
  license_plate: "",
  site: "",
};

export default function DialogCreateAsset(props) {
  const { open, onClose } = props;
  const { companyId, CreateAsset, loading } = useContext(DataSourceContext);
  const { plantsList } = usePlantsList(companyId);
  const [formData, setFormData] = useState({
    ...INITFORMDATA,
    company_id: companyId,
    plant_id: plantsList?.plants[0]?.id,
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      company_id: companyId,
      plant_id: plantsList?.plants[0]?.id,
    }));
  }, [companyId, plantsList]);

  const _plants = useMemo(() => {
    return plantsList?.plants;
  }, [plantsList]);

  const handlePlantChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      plant_id: value?.id,
    }));
  };

  const handleNameChange = (value, key) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDescriptionChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  };

  const handleLicenseChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      license_plate: e.target.value,
    }));
  };

  const handleSiteChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      site: e.target.value,
    }));
  };

  const handleClose = () => {
    setFormData(INITFORMDATA);
    onClose();
  };

  const handleCreateAsset = async () => {
    const response = await CreateAsset(formData);
    if (response.status === 200 || response.status === 201) {
      setFormData(INITFORMDATA);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          height: "fit-content",
        },
      }}
    >
      {loading?.isLoadingCreateAsset && (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            position: "absolute",
            backdropFilter: "blur(0.6px)",
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={80} />
        </Box>
      )}
      <DialogTitle>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Create New Asset
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: "100%",
            margin: 0,
            display: "grid",
            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
            gap: "24px",
          }}
        >
          <Box
            sx={{
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gridColumn: "1 / span 4",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                padding: "1px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F2F8F6",
                  border: "2px dashed #96D3C0",
                  borderRadius: "9px",
                  gap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "semibold", textAlign: "center" }}
                >
                  Drag and drop to upload asset image
                </Typography>
                <Button
                  sx={{
                    backgroundColor: "#21A27F",
                    color: "white",
                    borderRadius: "16px",
                    padding: "4px 12px",
                    "&:hover": {
                      backgroundColor: "#3dbb73",
                    },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    Browse image(s)
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ gridColumn: "span 8 / span 8" }}>
            <FormGroup sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Plant
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                <Autocomplete
                  options={_plants}
                  getOptionLabel={(option) => option.name}
                  defaultValue={_plants && _plants[0]}
                  onChange={(e, value) => {
                    handlePlantChange(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select plant"
                      sx={{
                        ".MuiInputBase-root": {
                          padding: "4px 9px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <Box key={`plant-${option.id}`} {...rest}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          // sx={{ fontWeight: 600 }}
                        >
                          {option.name}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </Box>
            </FormGroup>

            <FormGroup>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Asset information
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={1}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    padding: "4px 8px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",

                    "&:focus-within": {
                      borderColor: "#3dbb73",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: "600" }}
                  >
                    Name (English)*
                  </Typography>
                  <TextField
                    name="asset_name_en"
                    variant="outlined"
                    value={formData.name_en}
                    placeholder="Enter asset name less than 60 characters"
                    onChange={(e) =>
                      handleNameChange(e.target.value, "name_en")
                    }
                    sx={{
                      display: "block",
                      ".MuiInputBase-root": {
                        width: "100%",
                        "& > .MuiInputBase-input": {
                          padding: "0",
                          marginBottom: 1.5,
                          fontSize: "14px",
                        },
                      },
                      ".Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "0",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        border: 0,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    padding: "4px 8px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",

                    "&:focus-within": {
                      borderColor: "#3dbb73",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Name (Thai)*
                  </Typography>
                  <TextField
                    name="asset_name_th"
                    variant="outlined"
                    value={formData.name_th}
                    placeholder="Enter asset name less than 60 characters"
                    onChange={(e) =>
                      handleNameChange(e.target.value, "name_th")
                    }
                    sx={{
                      display: "block",
                      ".MuiInputBase-root": {
                        width: "100%",
                        "& > .MuiInputBase-input": {
                          padding: "0",
                          marginBottom: 1.5,
                          fontSize: "14px",
                        },
                      },
                      ".Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "0",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        border: 0,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={1}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    padding: "4px 8px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",

                    "&:focus-within": {
                      borderColor: "#3dbb73",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Description
                  </Typography>
                  <TextField
                    name="description"
                    variant="outlined"
                    value={formData.description}
                    placeholder="Enter asset description less than 500 characters"
                    onChange={handleDescriptionChange}
                    sx={{
                      display: "block",
                      ".MuiInputBase-root": {
                        width: "100%",
                        "& > .MuiInputBase-input": {
                          padding: "0",
                          marginBottom: 1.5,
                          fontSize: "14px",
                        },
                      },
                      ".Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "0",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        border: 0,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    padding: "4px 8px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",

                    "&:focus-within": {
                      borderColor: "#3dbb73",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 600 }}
                  >
                    S/N Or License Plate
                  </Typography>
                  <TextField
                    name="license_plate"
                    variant="outlined"
                    value={formData.license_plate}
                    placeholder="Enter S/N License plate"
                    onChange={handleLicenseChange}
                    sx={{
                      display: "block",
                      ".MuiInputBase-root": {
                        width: "100%",
                        "& > .MuiInputBase-input": {
                          padding: "0",
                          marginBottom: 1.5,
                          fontSize: "14px",
                        },
                      },
                      ".Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "0",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        border: 0,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={1}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    padding: "4px 8px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",

                    "&:focus-within": {
                      borderColor: "#3dbb73",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Site
                  </Typography>
                  <TextField
                    name="site"
                    variant="outlined"
                    value={formData.site}
                    placeholder="Select site or location"
                    onChange={handleSiteChange}
                    sx={{
                      display: "block",
                      ".MuiInputBase-root": {
                        width: "100%",
                        "& > .MuiInputBase-input": {
                          padding: "0",
                          marginBottom: 1.5,
                          fontSize: "14px",
                        },
                      },
                      ".Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "0",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        border: 0,
                      },
                    }}
                  />
                </Box>
              </Box>
            </FormGroup>
          </Box>
        </Box>
        {/* <Box className="bg-vekin-green-40">Items</Box> */}
      </DialogContent>
      <DialogAction>
        <Stack direction="row" spacing="8px">
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              color: "gray",
              borderColor: "gray",
              "&:hover": {
                borderColor: "gray",
                backgroundColor: "transparent",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            // disabled={true}
            onClick={handleCreateAsset}
            sx={{
              backgroundColor: "gray",
              color: "white",
              "&:hover": {
                backgroundColor: "gray",
              },
            }}
          >
            Create
          </Button>
        </Stack>
      </DialogAction>
    </Dialog>
  );
}
